// src/components/FrontOfficeComp/DocumentsTab.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE } from '../../config/api.js';
import {
  ALLOWED_DOC_EXTENSIONS,
  DEFAULT_MAX_DOC_SIZE_BYTES,
  deleteDocument,
  formatFileSize,
  getDocumentActivity,
  getDocumentDownloadUrl,
  getFileExtension,
  isAllowedDocFile,
  listDocuments,
  logDocumentView,
  uploadDocuments,
} from '../../services/documentsService.js';

// Fallback if the API isn't reachable. Mirrors initial tblgeneric seed.
const FALLBACK_DOCUMENT_TYPES = [
  'CNIC',
  'Court Order',
  'FIR',
  'Medical Report',
  'Affidavit',
  'Agreement',
  'Photo Evidence',
  'Other',
];

const acceptAttribute = ALLOWED_DOC_EXTENSIONS.map((ext) => `.${ext}`).join(',');

const formatTimestamp = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-PK', {
    timeZone: 'Asia/Karachi',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
};

const DocumentsTab = ({
  queryId,
  uploadedBy = '',
  userId = '',
  userName = '',
  maxFileSizeBytes = DEFAULT_MAX_DOC_SIZE_BYTES,
}) => {
  const trimmedQueryId = String(queryId ?? '').trim();
  const hasQueryId = Boolean(trimmedQueryId);
  const trimmedUserId = String(userId || uploadedBy || '').trim();
  const trimmedUserName = String(userName || uploadedBy || '').trim();

  const fileInputRef = useRef(null);
  const [documents, setDocuments] = useState([]);
  const [documentTypeOptions, setDocumentTypeOptions] = useState(FALLBACK_DOCUMENT_TYPES);
  const [documentType, setDocumentType] = useState(FALLBACK_DOCUMENT_TYPES[0]);
  const [notes, setNotes] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [loadError, setLoadError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  // History modal state
  const [historyDoc, setHistoryDoc] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  const maxSizeMb = useMemo(
    () => Math.max(1, Math.round((maxFileSizeBytes / (1024 * 1024)) * 10) / 10),
    [maxFileSizeBytes]
  );

  const refreshDocuments = useCallback(async () => {
    if (!trimmedQueryId) {
      setDocuments([]);
      return;
    }
    setIsLoading(true);
    setLoadError('');
    try {
      const list = await listDocuments(trimmedQueryId, { userId: trimmedUserId });
      setDocuments(Array.isArray(list) ? list : []);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) {
        // No documents yet — treat as empty list, not an error.
        setDocuments([]);
      } else {
        setLoadError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to load documents.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [trimmedQueryId, trimmedUserId]);

  useEffect(() => {
    refreshDocuments();
  }, [refreshDocuments]);

  // Fetch document types from tblgeneric (headername=Complaint,
  // sectionname=WhatsappDocumentType). Falls back to the hard-coded list
  // if the request fails so the UI remains usable offline.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/document-types`);
        if (!res.ok) return;
        const data = await res.json();
        const list =
          (Array.isArray(data) && data) ||
          (Array.isArray(data?.data) && data.data) ||
          (Array.isArray(data?.value) && data.value) ||
          [];
        const values = list
          .map((item) => (typeof item === 'string' ? item : item?.value))
          .map((v) => (typeof v === 'string' ? v.trim() : ''))
          .filter(Boolean);
        if (cancelled || values.length === 0) return;
        setDocumentTypeOptions(values);
        setDocumentType((current) =>
          values.includes(current) ? current : values[0]
        );
      } catch (err) {
        // Silent — keep using FALLBACK_DOCUMENT_TYPES.
        console.warn('Failed to load document types:', err?.message || err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const validateFiles = useCallback(
    (files) => {
      const errors = [];
      if (!files.length) {
        errors.push('Please select at least one file.');
        return errors;
      }
      files.forEach((file) => {
        if (!isAllowedDocFile(file)) {
          errors.push(
            `"${file.name}" has an unsupported type. Allowed: ${ALLOWED_DOC_EXTENSIONS.join(', ')}.`
          );
        } else if (file.size > maxFileSizeBytes) {
          errors.push(
            `"${file.name}" exceeds the ${maxSizeMb} MB size limit.`
          );
        }
      });
      return errors;
    },
    [maxFileSizeBytes, maxSizeMb]
  );

  const handleFilesSelected = useCallback(
    (event) => {
      const list = Array.from(event.target.files || []);
      setSelectedFiles(list);
      setStatusMessage('');
      setValidationErrors(list.length ? validateFiles(list) : []);
    },
    [validateFiles]
  );

  const handleClearSelection = useCallback(() => {
    setSelectedFiles([]);
    setValidationErrors([]);
    setStatusMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleUpload = useCallback(
    async (event) => {
      event.preventDefault();
      setStatusMessage('');

      const errors = [];
      if (!hasQueryId) {
        errors.push('Save the query first — a Query ID is required before uploading.');
      }
      errors.push(...validateFiles(selectedFiles));
      if (errors.length) {
        setValidationErrors(errors);
        return;
      }

      setValidationErrors([]);
      setIsUploading(true);
      try {
        await uploadDocuments(trimmedQueryId, {
          files: selectedFiles,
          documentType,
          notes,
          uploadedBy,
        });
        setStatusMessage(
          `Uploaded ${selectedFiles.length} file${selectedFiles.length === 1 ? '' : 's'} successfully.`
        );
        setNotes('');
        handleClearSelection();
        await refreshDocuments();
      } catch (err) {
        setValidationErrors([
          err?.response?.data?.message ||
            err?.message ||
            'Upload failed. Please try again.',
        ]);
      } finally {
        setIsUploading(false);
      }
    },
    [
      documentType,
      handleClearSelection,
      hasQueryId,
      notes,
      refreshDocuments,
      selectedFiles,
      trimmedQueryId,
      uploadedBy,
      validateFiles,
    ]
  );

  const handleDelete = useCallback(
    async (doc) => {
      if (!doc?.id) return;
      const confirmed = window.confirm(
        `Delete "${doc.fileName || doc.name || 'this document'}"?`
      );
      if (!confirmed) return;
      setDeletingId(String(doc.id));
      try {
        await deleteDocument(trimmedQueryId, doc.id);
        setStatusMessage('Document deleted.');
        await refreshDocuments();
      } catch (err) {
        setLoadError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to delete document.'
        );
      } finally {
        setDeletingId('');
      }
    },
    [refreshDocuments, trimmedQueryId]
  );

  const resolvePreviewUrl = useCallback(
    (doc) => {
      if (!doc) return '';
      if (doc.url) return doc.url;
      return getDocumentDownloadUrl(trimmedQueryId, doc.id);
    },
    [trimmedQueryId]
  );

  const resolveDownloadUrl = useCallback(
    (doc) => {
      if (!doc?.id) return '';
      return getDocumentDownloadUrl(trimmedQueryId, doc.id, {
        userId: trimmedUserId,
        userName: trimmedUserName,
      });
    },
    [trimmedQueryId, trimmedUserId, trimmedUserName]
  );

  // File name click: log the view (fire-and-forget) then open the file in
  // a new tab. On failure to log, we still open the file so the user is
  // never blocked.
  const handleViewFile = useCallback(
    async (doc, event) => {
      if (event) {
        event.preventDefault();
      }
      const docId = doc?.id ?? doc?.documentId ?? '';
      const previewUrl = resolvePreviewUrl(doc);
      if (!previewUrl) return;

      if (docId && trimmedQueryId) {
        try {
          await logDocumentView(trimmedQueryId, docId, {
            userId: trimmedUserId,
            userName: trimmedUserName,
          });
        } catch (err) {
          // Never block the user — just warn in console.
          console.warn('Failed to log document view:', err?.message || err);
        }
        // Optimistic local update: mark as viewed unless already downloaded.
        setDocuments((prev) =>
          prev.map((d) => {
            if (String(d.id ?? d.documentId) !== String(docId)) return d;
            if (d.activityStatus === 'downloaded') return d;
            return { ...d, activityStatus: 'viewed' };
          })
        );
      }

      try {
        window.open(previewUrl, '_blank', 'noopener,noreferrer');
      } catch {
        window.location.href = previewUrl;
      }
    },
    [resolvePreviewUrl, trimmedQueryId, trimmedUserId, trimmedUserName]
  );

  // Download click handler — uses anchor href but also optimistically
  // marks the doc as downloaded so the badge updates immediately.
  const handleDownloadClick = useCallback(
    (doc) => {
      const docId = doc?.id ?? doc?.documentId ?? '';
      if (!docId) return;
      setDocuments((prev) =>
        prev.map((d) =>
          String(d.id ?? d.documentId) === String(docId)
            ? { ...d, activityStatus: 'downloaded' }
            : d
        )
      );
    },
    []
  );

  const openHistory = useCallback(
    async (doc) => {
      const docId = doc?.id ?? doc?.documentId ?? '';
      if (!docId || !trimmedQueryId) return;
      setHistoryDoc({ id: docId, fileName: doc.fileName || doc.name || 'Document' });
      setHistoryData(null);
      setHistoryError('');
      setHistoryLoading(true);
      try {
        const data = await getDocumentActivity(trimmedQueryId, docId);
        setHistoryData(data);
      } catch (err) {
        setHistoryError(
          err?.response?.data?.message ||
            err?.message ||
            'Failed to load activity history.'
        );
      } finally {
        setHistoryLoading(false);
      }
    },
    [trimmedQueryId]
  );

  const closeHistory = useCallback(() => {
    setHistoryDoc(null);
    setHistoryData(null);
    setHistoryError('');
  }, []);

  return (
    <div className="p-2">
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div>
          <h6 className="fw-semibold mb-0">Documents Received on WhatsApp</h6>
          <small className="text-muted">
            {hasQueryId
              ? `Linked to Query ID: ${trimmedQueryId}`
              : 'Save the query to enable uploads.'}
          </small>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={refreshDocuments}
          disabled={!hasQueryId || isLoading}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </button>
      </div>

      {!hasQueryId && (
        <div className="alert alert-warning py-2 small mb-2">
          A Query ID is required before uploading documents. Save or open an existing query first.
        </div>
      )}

      <form onSubmit={handleUpload} className="border rounded p-2 mb-3 bg-light">
        <div className="row g-2">
          <div className="col-12 col-md-4">
            <label className="form-label small mb-1">Document Type</label>
            <select
              className="form-select form-select-sm"
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              disabled={!hasQueryId || isUploading}
            >
              {documentTypeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-8">
            <label className="form-label small mb-1">Notes (optional)</label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Short note about these files"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!hasQueryId || isUploading}
              maxLength={250}
            />
          </div>
          <div className="col-12">
            <label className="form-label small mb-1">
              Files <span className="text-muted">({ALLOWED_DOC_EXTENSIONS.join(', ')} — max {maxSizeMb} MB each)</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              className="form-control form-control-sm"
              multiple
              accept={acceptAttribute}
              onChange={handleFilesSelected}
              disabled={!hasQueryId || isUploading}
            />
          </div>

          {selectedFiles.length > 0 && (
            <div className="col-12">
              <ul className="list-group list-group-flush small">
                {selectedFiles.map((file) => (
                  <li
                    key={`${file.name}-${file.size}-${file.lastModified}`}
                    className="list-group-item py-1 px-2 d-flex justify-content-between bg-transparent"
                  >
                    <span className="text-truncate me-2">{file.name}</span>
                    <span className="text-muted">{formatFileSize(file.size)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {validationErrors.length > 0 && (
            <div className="col-12">
              <div className="alert alert-danger py-2 mb-0 small">
                {validationErrors.map((err, idx) => (
                  <div key={idx}>{err}</div>
                ))}
              </div>
            </div>
          )}

          {statusMessage && (
            <div className="col-12">
              <div className="alert alert-success py-2 mb-0 small">{statusMessage}</div>
            </div>
          )}

          <div className="col-12 d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={handleClearSelection}
              disabled={isUploading || selectedFiles.length === 0}
            >
              Clear
            </button>
            <button
              type="submit"
              className="btn btn-sm btn-primary"
              disabled={!hasQueryId || isUploading || selectedFiles.length === 0}
            >
              {isUploading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Uploading…
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-upload me-1"></i>
                  Upload
                </>
              )}
            </button>
          </div>
        </div>
      </form>

      <div className="d-flex justify-content-between align-items-center mb-1">
        <strong className="small">Uploaded Files</strong>
        <small className="text-muted">{documents.length} item{documents.length === 1 ? '' : 's'}</small>
      </div>

      {loadError && (
        <div className="alert alert-danger py-2 small mb-2">{loadError}</div>
      )}

      <div className="border rounded" style={{ maxHeight: '40vh', overflow: 'auto' }}>
        {isLoading ? (
          <div className="text-center text-muted py-3 small">Loading documents…</div>
        ) : documents.length === 0 ? (
          <div className="text-center text-muted py-3 small">
            {hasQueryId ? 'No documents uploaded yet.' : 'Open a query to view documents.'}
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover mb-0 small align-middle">
              <thead className="table-light">
                <tr>
                  <th>File</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => {
                  const docId = doc.id ?? doc.documentId ?? doc.fileId ?? '';
                  const fileName = doc.fileName || doc.name || 'Untitled';
                  const ext = getFileExtension(fileName) || (doc.mimeType || '').split('/').pop() || '';
                  const previewUrl = resolvePreviewUrl(doc);
                  const downloadUrl = resolveDownloadUrl({ ...doc, id: docId });
                  const isDeleting = String(docId) === deletingId;
                  const activityStatus = String(doc.activityStatus || 'not_viewed').toLowerCase();
                  const statusBadge =
                    activityStatus === 'downloaded'
                      ? { cls: 'bg-success', label: 'Downloaded' }
                      : activityStatus === 'viewed'
                      ? { cls: 'bg-info text-dark', label: 'Viewed' }
                      : { cls: 'bg-secondary', label: 'Not Viewed' };
                  const activityCount = Number(doc.activityCount || 0);
                  return (
                    <tr key={String(docId) || fileName}>
                      <td className="text-truncate" style={{ maxWidth: '220px' }}>
                        {previewUrl ? (
                          <a
                            href={previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={fileName}
                            onClick={(e) => handleViewFile({ ...doc, id: docId, fileName }, e)}
                          >
                            {fileName}
                          </a>
                        ) : (
                          <span title={fileName}>{fileName}</span>
                        )}
                        {doc.notes && (
                          <div className="text-muted small text-truncate" title={doc.notes}>
                            {doc.notes}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className="badge bg-secondary text-uppercase">{ext || '—'}</span>
                        {doc.documentType && (
                          <div className="text-muted small">{doc.documentType}</div>
                        )}
                      </td>
                      <td>{formatFileSize(doc.size)}</td>
                      <td>
                        <div>{formatTimestamp(doc.uploadedAt || doc.createdAt)}</div>
                        {doc.uploadedBy && (
                          <small className="text-muted">{doc.uploadedBy}</small>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${statusBadge.cls}`}>{statusBadge.label}</span>
                      </td>
                      <td className="text-end">
                        {downloadUrl && (
                          <a
                            className="btn btn-sm btn-outline-primary me-1"
                            href={downloadUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Download"
                            onClick={() => handleDownloadClick({ ...doc, id: docId })}
                          >
                            <i className="bi bi-download"></i>
                          </a>
                        )}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary me-1"
                          onClick={() => openHistory({ ...doc, id: docId, fileName })}
                          disabled={!docId}
                          title={`Activity history${activityCount ? ` (${activityCount})` : ''}`}
                        >
                          <i className="bi bi-clock-history"></i>
                          {activityCount > 0 && (
                            <span className="ms-1 small">{activityCount}</span>
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete({ ...doc, id: docId, fileName })}
                          disabled={!docId || isDeleting}
                          title="Delete"
                        >
                          {isDeleting ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            <i className="bi bi-trash"></i>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {historyDoc && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeHistory();
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
            <div className="modal-content">
              <div className="modal-header py-2">
                <h6 className="modal-title">
                  Activity history
                  <span className="text-muted ms-2 small">{historyDoc.fileName}</span>
                </h6>
                <button type="button" className="btn-close" aria-label="Close" onClick={closeHistory}></button>
              </div>
              <div className="modal-body p-2">
                {historyLoading && (
                  <div className="text-center text-muted py-3 small">Loading activity…</div>
                )}
                {historyError && !historyLoading && (
                  <div className="alert alert-danger py-2 small mb-2">{historyError}</div>
                )}
                {!historyLoading && !historyError && historyData && (
                  <>
                    <div className="d-flex gap-3 mb-2 small">
                      <span className="badge bg-info text-dark">
                        Views: {historyData?.counts?.views ?? 0}
                      </span>
                      <span className="badge bg-success">
                        Downloads: {historyData?.counts?.downloads ?? 0}
                      </span>
                      <span className="badge bg-secondary">
                        Total: {historyData?.counts?.total ?? 0}
                      </span>
                    </div>
                    {Array.isArray(historyData.activities) && historyData.activities.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm small mb-0 align-middle">
                          <thead className="table-light">
                            <tr>
                              <th>Action</th>
                              <th>User</th>
                              <th>When</th>
                              <th>IP</th>
                              <th>Browser</th>
                            </tr>
                          </thead>
                          <tbody>
                            {historyData.activities.map((entry) => (
                              <tr key={entry.id}>
                                <td>
                                  <span
                                    className={`badge ${
                                      entry.actionType === 'download' ? 'bg-success' : 'bg-info text-dark'
                                    } text-capitalize`}
                                  >
                                    {entry.actionType}
                                  </span>
                                </td>
                                <td>
                                  <div>{entry.userName || entry.userId || '—'}</div>
                                  {entry.userId && entry.userName && entry.userId !== entry.userName && (
                                    <small className="text-muted">{entry.userId}</small>
                                  )}
                                </td>
                                <td>{formatTimestamp(entry.createdAt)}</td>
                                <td className="text-muted">{entry.ipAddress || '—'}</td>
                                <td
                                  className="text-muted text-truncate"
                                  style={{ maxWidth: '240px' }}
                                  title={entry.userAgent || ''}
                                >
                                  {entry.userAgent || '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center text-muted py-3 small">
                        No activity yet for this document.
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer py-2">
                <button type="button" className="btn btn-sm btn-secondary" onClick={closeHistory}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsTab;
